import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TenantProvider } from "@/core/tenant/TenantProvider";
import { RealtimeProvider } from "@/core/realtime/RealtimeProvider";
import { AppShell } from "@/core/layout/AppShell";
import { PuzzleListPage } from "@/core/puzzles/PuzzleListPage";
import { PuzzlePreviewPage } from "@/core/puzzles/PuzzlePreviewPage";
import { BacklogPage } from "@/core/backlog/BacklogPage";
import { PuzzleWorkPage } from "@/core/backlog/PuzzleWorkPage";
import { PlayPage } from "@/core/play/PlayPage";
import { ExamplePage } from "@/core/play/ExamplePage";
import { SheetPage } from "@/core/sheet/SheetPage";
import { LoginPage } from "@/core/auth/LoginPage";
import { AuthCallbackPage } from "@/core/auth/AuthCallbackPage";
import { LoginLinkPage } from "@/core/auth/LoginLinkPage";
import { RequireAuth } from "@/core/auth/RequireAuth";
import { AdminPage } from "@/core/admin/AdminPage";
import { AccountPage } from "@/core/account/AccountPage";
import { SeasonListPage } from "@/core/seasons/SeasonListPage";
import { SeasonStandingsPage } from "@/core/seasons/SeasonStandingsPage";
import { TeamsPage } from "@/core/teams/TeamsPage";
import { GamesPage } from "@/core/games/GamesPage";
import { GameRunPage } from "@/core/games/GameRunPage";
import { DuelLobbyPage } from "@/core/duels/DuelLobbyPage";
import { DuelPage } from "@/core/duels/DuelPage";
import { useThemeEffect } from "@/core/theme";
import { ToastProvider } from "@/core/ui";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function NotFound() {
  const { t } = useTranslation();
  return <p className="bt-page-message">{t("app.not_found")}</p>;
}

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: "/", element: <PuzzleListPage /> },
      { path: "/auth/login", element: <LoginPage /> },
      { path: "/auth/callback", element: <AuthCallbackPage /> },
      { path: "/auth/link", element: <LoginLinkPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: "/account", element: <AccountPage /> },
          { path: "/puzzles/:slug", element: <PlayPage /> },
          { path: "/examples/:type", element: <ExamplePage /> },
          { path: "/seasons", element: <SeasonListPage /> },
          { path: "/seasons/:id", element: <SeasonStandingsPage /> },
          { path: "/teams", element: <TeamsPage /> },
          { path: "/games", element: <GamesPage /> },
          { path: "/games/:key", element: <GameRunPage /> },
          { path: "/duels/:key", element: <DuelLobbyPage /> },
          { path: "/duels/:key/:id", element: <DuelPage /> },
        ],
      },
      {
        element: <RequireAuth roles={["author", "reviewer", "tenant_admin"]} />,
        children: [
          { path: "/puzzles/:slug/preview", element: <PuzzlePreviewPage /> },
          { path: "/backlog", element: <BacklogPage /> },
          { path: "/backlog/:slug", element: <PuzzleWorkPage /> },
        ],
      },
      {
        element: <RequireAuth roles={["tenant_admin"]} />,
        children: [{ path: "/admin", element: <AdminPage /> }],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
  {
    // The PDF sheet is a document, not a screen: no header, no navigation, no footer.
    element: <RequireAuth />,
    children: [{ path: "/puzzles/:slug/sheet", element: <SheetPage /> }],
  },
]);

export function App() {
  // Applies the stored theme and keeps the "system" preference tracking the OS.
  // Outside TenantProvider on purpose: the theme must hold on the loading and
  // "unknown tenant" screens too, which render before branding lands.
  useThemeEffect();
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <TenantProvider>
          <RealtimeProvider>
            <RouterProvider router={router} />
          </RealtimeProvider>
        </TenantProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
